// TenderOverview — one tender, end to end: bids, the opening ceremony, the matrix, and the scores
// with their disposition against the AI first cut.
//
// Connects to: GET /api/townforge/tenders/{orgId}/{id}/overview. One request, because the page's
// correctness depends on every part agreeing about whether the box is open — six independent
// fetches can interleave with an opening ceremony and render a half-opened tender.
//
// Two things this screen exists to make visible, which are otherwise only true in the schema:
//
//   1. THE SEAL (design-principles.md §2.1). Before opening, a submission renders as a present,
//      deliberate, locked row — tenderer, arrival time, seal mark — never an empty box. "Something
//      is here and it is protected" is a different statement from "nothing here", and an officer
//      must be able to see the control working. The server sends no sum before opening, so there
//      is nothing here to leak even by accident.
//   2. LAW 1. Every score is shown beside what the AI proposed and how a named human disposed of
//      it — accepted, adjusted with a reason, or scored with no draft at all. An AI number that
//      nobody accepted is not a score, and this is where that stops being an abstraction.
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { api, ApiError, type TenderOverview as Overview } from '../lib/api'

function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return `$${v.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function when(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-SG', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  })
}

const SEVERITY_TONE: Record<string, string> = { info: 'neutral', warn: 'warn', high: 'danger' }

const STATE_LABEL: Record<string, string> = {
  draft: 'Draft', internal_review: 'Internal review', published: 'Published', open: 'Open',
  closed: 'Closed', opened: 'Opened', clarification: 'Clarification', evaluation: 'Evaluation',
  recommendation: 'Recommendation', approval: 'Approval', awarded: 'Awarded',
  not_awarded: 'Not awarded', cancelled: 'Cancelled',
}

// One-way doors. design-principles.md §2.3: a confirmation must name WHAT BECOMES IRREVERSIBLE,
// not ask "are you sure" — which tells the officer nothing they did not already know.
const IRREVERSIBLE: Record<string, string> = {
  published:
    'Publishing locks the requirement set and the evaluation matrix. They can then change only by '
    + 'addendum, and never after the tender closes. Continue?',
  open:
    'Opening the tender to submissions starts the clock. Tenderers may register and submit from '
    + 'this moment. Continue?',
  closed:
    'Closing seals the tender box. No further submission can be accepted, and the server clock '
    + 'decides admissibility to the second. Continue?',
  opened:
    'This is the opening ceremony: the seal is broken and every bid becomes readable. It cannot be '
    + 'resealed. Continue?',
  cancelled:
    'Cancelling ends this tender. It cannot be reopened, and any submissions already received stay '
    + 'on the record. Continue?',
}

/** How a score relates to the AI proposal. The wording is deliberate: "Accepted" and "Adjusted"
 *  describe what the HUMAN did, never what the model produced. */
const DISPOSITION: Record<string, string> = {
  accepted: 'Accepted AI proposal',
  adjusted: 'Adjusted AI proposal',
  rejected: 'Rejected AI proposal',
  original: 'Scored without AI',
}

function Section({ title, note, children }: {
  title: string; note?: string; children: React.ReactNode
}) {
  return (
    <section className="sec">
      <h2 className="sec-title">{title}</h2>
      {note && <p className="sec-note">{note}</p>}
      {/* Content indented under its heading, per design-principles.md §1.7 — composed once here
          rather than hand-rolled per section, which is how sibling sections drift apart. */}
      <div className="sec-body">{children}</div>
    </section>
  )
}

export default function TenderOverviewScreen() {
  const { id } = useParams<{ id: string }>()
  const { me } = useAuth()
  const orgId = me?.org?.id ?? null

  const [ov, setOv] = useState<Overview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [acting, setActing] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (!orgId || !id) return
    let cancelled = false
    setOv(null); setError(null)
    api.tenders.overview(orgId, id)
      .then(r => { if (!cancelled) setOv(r) })
      .catch((e: unknown) => {
        if (cancelled) return
        // Reported, never substituted (TD-034). A tender page that renders empty because a fetch
        // failed would show an officer a tender with no bids.
        setError(e instanceof ApiError
          ? `Could not load this tender: ${e.detail}`
          : 'Could not reach TownForge.')
      })
    return () => { cancelled = true }
  }, [orgId, id, nonce])

  // Move the tender to its next lifecycle state. The server owns the rules — which transitions are
  // legal, and the preconditions on publishing (a requirement set must exist and the criterion
  // weights must total 100). This only asks; refusals are shown verbatim because the API names the
  // precondition that failed, which is what the officer has to fix.
  async function transition(to: string) {
    if (!orgId || !id) return
    const label = STATE_LABEL[to] ?? to
    if (IRREVERSIBLE[to] && !window.confirm(IRREVERSIBLE[to])) return
    setActing(to); setActionError(null)
    try {
      await api.tenders.transition(orgId, id, to)
      setNonce(n => n + 1)
    } catch (e: unknown) {
      setActionError(e instanceof ApiError
        ? `Could not move to ${label}: ${e.detail}`
        : 'Could not reach TownForge.')
    } finally {
      setActing(null)
    }
  }

  if (!orgId) return <div className="page"><p className="page-state">
    Your account is not a member of a Town Council.</p></div>
  if (error) return <div className="page"><p className="page-state page-state-error">{error}</p></div>
  if (!ov) return <div className="page"><p className="page-state">Loading…</p></div>

  const t = ov.tender
  const scoresBySub = new Map<string, Overview['scores']>()
  for (const s of ov.scores) {
    const list = scoresBySub.get(s.submissionId) ?? []
    list.push(s)
    scoresBySub.set(s.submissionId, list)
  }

  return (
    <div className="page">
      <header className="page-head">
        <div className="reg-ref mono">{t.tenderRef}</div>
        <h1>{t.title}</h1>
        <div className="facts">
          <span className="fact"><span className="reg-lbl">Status</span>
            <span className="fact-v">{t.status.replace(/_/g, ' ')}</span></span>
          <span className="fact"><span className="reg-lbl">Estimated</span>
            <span className="fact-v mono">{money(t.estimatedValue)}</span></span>
          <span className="fact"><span className="reg-lbl">Closed</span>
            <span className="fact-v">{when(t.closesAt)}</span></span>
          <span className="fact"><span className="reg-lbl">Period</span>
            <span className="fact-v">{t.contractPeriodMonths ?? '—'} months</span></span>
        </div>
      </header>

      {ov.nextStates.length > 0 && (
        <Section
          title="Move this tender forward"
          note={`Currently ${STATE_LABEL[t.status] ?? t.status}. Only the moves the lifecycle allows from here are offered, and the server refuses anything else.`}
        >
          <div className="actions">
            {ov.nextStates.map(st => (
              <button key={st} type="button" className="btn-quiet"
                      disabled={acting !== null}
                      onClick={() => transition(st)}>
                {acting === st ? 'Working…' : (STATE_LABEL[st] ?? st)}
              </button>
            ))}
          </div>
          {actionError && <p className="page-state page-state-error">{actionError}</p>}
        </Section>
      )}

      <Section
        title={ov.opened ? 'Bids' : 'Tender box'}
        note={ov.opened
          ? undefined
          : 'Sealed. Identities and arrival times are visible; content and sums are not, and the server does not send them before the opening ceremony.'}
      >
        {ov.submissions.length === 0 ? (
          <p className="muted">No submissions received.</p>
        ) : (
          <ul className="rows">
            {ov.submissions.map(s => (
              <li key={s.id} className="row">
                <div className="row-main">
                  <span className="row-title">{s.supplierName ?? s.supplierOrgId}</span>
                  <span className="row-sub">Received {when(s.submittedAt)}</span>
                  {s.responsive !== null && (
                    <span className={`row-sub ${s.responsive ? '' : 'tone-danger'}`}>
                      {s.responsive ? 'Responsive' : 'Not responsive'}
                      {s.responsiveReason ? ` — ${s.responsiveReason}` : ''}
                      {/* Which of the two decided it is not a detail: the machine may recommend,
                          a person determines. */}
                      {s.responsiveAuto === false ? ' (determined by an officer)' : ''}
                    </span>
                  )}
                </div>
                <div className="row-right">
                  {s.sealed
                    ? <span className="seal">SEALED until opening</span>
                    : <span className="row-num mono">{money(s.tenderSum)}</span>}
                  <span className="row-hash mono" title={s.sha256}>{s.sha256.slice(0, 12)}…</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {ov.opening && (
        <Section
          title="Opening ceremony"
          note="Two officers, each releasing one key share. One share alone cannot decrypt a bid, which is what makes the sealed box a cryptographic control rather than a permission flag."
        >
          <div className="facts">
            <span className="fact"><span className="reg-lbl">Officer A</span>
              <span className="fact-v">{ov.opening.officerA ?? '—'}</span></span>
            <span className="fact"><span className="reg-lbl">Officer B</span>
              <span className="fact-v">{ov.opening.officerB ?? '—'}</span></span>
            <span className="fact"><span className="reg-lbl">Completed</span>
              <span className="fact-v">{when(ov.opening.completedAt)}</span></span>
          </div>
          {ov.opening.lines.length > 0 && (
            <ul className="rows">
              {ov.opening.lines.map((l, i) => (
                <li key={i} className="row">
                  <div className="row-main">
                    <span className="row-title">{l.supplierName}</span>
                    {l.remark && <span className="row-sub">{l.remark}</span>}
                  </div>
                  <div className="row-right">
                    <span className="row-num mono">{money(l.tenderSum)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {ov.opening.recordSha256 && (
            <p className="muted mono small">Record hash {ov.opening.recordSha256.slice(0, 24)}…</p>
          )}
        </Section>
      )}

      {ov.matrix && (
        <Section
          title={`Evaluation matrix — version ${ov.matrix.version}`}
          note={ov.matrix.lockedAt
            ? `Locked ${when(ov.matrix.lockedAt)}. A matrix that could move once bids are visible would not be an evaluation.`
            : 'Not yet locked.'}
        >
          <ul className="rows">
            {ov.matrix.criteria.map(c => (
              <li key={c.id} className="row">
                <div className="row-main">
                  <span className="row-title">
                    <span className="mono row-sub">{c.critKey}</span> {c.title}
                  </span>
                  <span className="row-sub">{c.areaName}</span>
                </div>
                <div className="row-right"><span className="row-num mono">{c.weightPct}%</span></div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {ov.assignments.length > 0 && (
        <Section
          title="Evaluators"
          note="Each evaluator is scoped to named areas and sees only those. Scope is granted, never inherited."
        >
          <ul className="rows">
            {ov.assignments.map(a => (
              <li key={a.id} className="row">
                <div className="row-main">
                  <span className="row-title">{a.evaluatorName}</span>
                  <span className="row-sub">{a.areas.join(', ')} · {a.role?.replace(/_/g, ' ')}</span>
                </div>
                <div className="row-right"><span className="row-sub">{a.status?.replace(/_/g, ' ')}</span></div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {ov.opened && ov.scores.length > 0 && (
        <Section
          title="Scores"
          note="Every score names the human who set it and how they disposed of the AI proposal. An AI number nobody accepted is not a score."
        >
          {ov.submissions.filter(s => (scoresBySub.get(s.id) ?? []).length > 0).map(s => (
            <div key={s.id} className="scoreblock">
              <h3 className="scoreblock-h">{s.supplierName ?? s.supplierOrgId}</h3>
              <ul className="rows">
                {(scoresBySub.get(s.id) ?? []).map((sc, i) => (
                  <li key={i} className="row row-block">
                    <div className="row-main">
                      <span className="row-title">
                        <span className="mono row-sub">{sc.critKey}</span> {sc.critTitle}
                      </span>
                      {sc.comment && <span className="row-sub">{sc.comment}</span>}
                      <span className="row-sub">
                        {DISPOSITION[sc.disposition ?? ''] ?? sc.disposition}
                        {sc.aiProposedScore !== null && ` · AI proposed ${sc.aiProposedScore}`}
                        {' · '}{sc.scorerName}
                      </span>
                      {sc.changeReason && (
                        <span className="row-sub tone-warn">Why changed: {sc.changeReason}</span>
                      )}
                      {/* A citation an evaluator cannot check is a citation that cannot be relied
                          on (TD-015), so the passage travels with the proposal. */}
                      {sc.aiCitations?.map((c, j) => c.quote && (
                        <span key={j} className="cite">
                          “{c.quote}” — {c.doc}{c.page ? `, p.${c.page}` : ''}
                        </span>
                      ))}
                    </div>
                    <div className="row-right"><span className="row-num mono">{sc.score}</span></div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Section>
      )}

      {ov.probityFindings.length > 0 && (
        <Section
          title="Probity"
          note="A finding is surfaced for a human to dispose of. It is never an automatic disqualification."
        >
          <ul className="rows">
            {ov.probityFindings.map((p, i) => (
              <li key={i} className="row row-block">
                <div className="row-main">
                  <span className="row-title">
                    <span className={`tone tone-${SEVERITY_TONE[p.severity ?? ''] ?? 'neutral'}`}>
                      {p.severity}
                    </span>{' '}{p.kind.replace(/_/g, ' ')}
                  </span>
                  {p.detail?.summary && <span className="row-sub">{p.detail.summary}</span>}
                  {p.dispositionReason && (
                    <span className="row-sub">{p.disposition}: {p.dispositionReason}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {ov.coi.length > 0 && (
        <Section
          title="Conflict of interest"
          note="Declared before any bid content was seen."
        >
          <ul className="rows">
            {ov.coi.map((c, i) => (
              <li key={i} className="row row-block">
                <div className="row-main">
                  <span className="row-title">{c.userName} — {c.roleContext}</span>
                  <span className={`row-sub ${c.hasConflict ? 'tone-warn' : ''}`}>
                    {c.hasConflict ? 'Conflict declared' : 'No conflict declared'}
                    {c.disposition ? ` · ${c.disposition.replace(/_/g, ' ')}` : ''}
                  </span>
                  {c.dispositionReason && <span className="row-sub">{c.dispositionReason}</span>}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  )
}
